package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAvatar
import dev.lingvo.site.ds.LAvatarSize
import dev.lingvo.site.ds.LUserIcon
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.dom.Text

/**
 * Your own account. Never anybody else's.
 *
 * The name and the address are separated on purpose. A name is a display string and changing it
 * costs nothing; **the address is the account** — it is where password resets go — so it moves only
 * through a two-sided confirmation: a link to the new address and a notice to the old one. Folding
 * both into one "save profile" button is how a stolen session takes an account permanently.
 */
@Page("/app/account")
@Layout(".components.layouts.AppLayout")
@Composable
fun AccountPage() {
    val scope = rememberCoroutineScope()
    var refreshKey by remember { mutableStateOf(0) }
    // `loadReloadable`, not `loadAsync(refreshKey)`. The counter is bumped by a *successful* save,
    // and as a `loadAsync` key it re-keyed the `remember` — so the page discarded itself before the
    // re-read was even sent: the name field the reader was typing in, the address card and the
    // sign-in methods were all replaced by skeleton bars announcing "Loading your account", then
    // reassembled. The subject here is the session, and the counter is a reload token: the last
    // good account stays on screen while it is re-read.
    val reloadable = loadReloadable(Session.requestIdentity, reloadToken = refreshKey) {
        ApiClient.me()
    }
    val state = reloadable.state

    var name by remember { mutableStateOf<String?>(null) }
    var newEmail by remember { mutableStateOf("") }
    // One flag per action. Shared, pressing Save put a spinner on "Send the link" and disabled
    // "Cancel a pending change", both reporting a request that was not in flight — on the page
    // where the request in flight decides who owns the account.
    var savingName by remember { mutableStateOf(false) }
    var changingEmail by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf<String?>(null) }
    var sent by remember { mutableStateOf<String?>(null) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.account.account", "Account"),
            t("app.account.who_you_are_here_and_where", "Who you are here, and where we reach you."),
            mark = { LUserIcon() },
        )

        // Both of these are written only by a button handler, and both render at the top of the
        // page while the buttons that set them are inside cards below the fold. Without `live`,
        // pressing "Send the link" announced nothing at all.
        message?.let {
            LAlert(
                t("app.account.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        sent?.let {
            LAlert(
                t("app.account.check_your_inbox", "Check your inbox"),
                it,
                variant = LAlertVariant.SUCCESS,
                live = true,
            )
        }

        // A refresh that failed over an account that is still on screen. It belongs beside the last
        // good value rather than replacing it — the save it followed did succeed, and blanking the
        // page to report the re-read would be the very failure this page was fixed for.
        reloadable.refreshError?.let { failure ->
            LAlert(
                t("app.account.the_refresh_did_not_replace_this", "The refresh did not replace this account"),
                failure.message,
                variant = LAlertVariant.WARNING,
                live = true,
                dense = true,
            )
        }

        LAsyncContent(
            state,
            rows = 2,
            loadingLabel = t("app.account.loading_your_account", "Loading your account"),
            onRetry = { refreshKey++ },
        ) { loaded ->
            val me = loaded?.user ?: return@LAsyncContent
            val current = name ?: me.name

            Column(Modifier.fillMaxWidth().gap(Space.XL)) {
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        Row(Modifier.gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                            // The same mark the avatar menu draws, so the page about who you are
                            // shows the thing everyone else sees beside your name.
                            LAvatar(me.name, size = LAvatarSize.LG)
                            LSectionTitle(t("app.account.name", "Name"))
                        }
                        LTextField(current, { name = it }, Modifier.fillMaxWidth(), label = t("app.account.display_name", "Display name"))
                        LMuted(t("app.account.shown_on_your_comments_in_the", "Shown on your comments, in the activity log and beside your reviews."))
                        LButton(
                            t("app.account.save", "Save"),
                            {
                                scope.launch {
                                    savingName = true
                                    // Not `runCatching`: it swallows the CancellationException
                                    // Compose throws when this page leaves composition, and it
                                    // reported that as a failed save. `Session.loadMe` also ran
                                    // inside its success arm rather than under a `finally`, so a
                                    // throw there left every control on the page spinning until
                                    // somebody reloaded.
                                    try {
                                        ApiClient.updateProfile(current.trim())
                                        Session.loadMe()
                                        message = null
                                        refreshKey++
                                    } catch (cancelled: CancellationException) {
                                        throw cancelled
                                    } catch (failure: Throwable) {
                                        // A failure whose `message` is null used to clear the
                                        // alert instead of raising one, so the save failed in
                                        // silence.
                                        message = failure.message
                                            ?: tr("app.account.couldnt_save_that_name_retry", "Couldn't save that name. Retry?")
                                    } finally {
                                        savingName = false
                                    }
                                }
                            },
                            enabled = current.isNotBlank() && current != me.name && !savingName,
                            loading = savingName,
                        )
                    }
                }

                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LSectionTitle(t("app.account.email_address", "Email address"))
                        LMuted(me.email)
                        LTextField(
                            newEmail,
                            { newEmail = it },
                            Modifier.fillMaxWidth(),
                            label = t("app.account.change_it_to", "Change it to"),
                            placeholder = t("app.account.you_company_com", "you@company.com"),
                        )
                        // The two-sidedness is the security property, so it is stated rather than
                        // being a surprise after the button is pressed.
                        LMuted(
                            t("app.account.nothing_changes_until_you_follow_the_2", "Nothing changes until you follow the link we send to the new address. " +
                                "We also tell the old one, so a change you did not ask for is not a " +
                                "change you find out about when your password reset stops arriving."),
                        )
                        // Wraps, because at 390 it cannot not. Two fixed-width buttons on one
                        // unwrapping row shrank until "Send the / link" broke inside the accent
                        // button and "Cancel a / pending change" broke beside it — the page's one
                        // primary action rendered as two lines of torn label in a 64px block.
                        // Below MD the secondary drops underneath and both labels stay whole.
                        Row(Modifier.gap(Space.SM).flexWrap(FlexWrap.Wrap)) {
                            LButton(
                                t("app.account.send_the_link", "Send the link"),
                                {
                                    val address = newEmail.trim()
                                    scope.launch {
                                        changingEmail = true
                                        sent = null
                                        try {
                                            ApiClient.requestEmailChange(address)
                                            sent = trf(
                                                "app.account.we_sent_a_confirmation_link_to",
                                                "We sent a confirmation link to {email}.",
                                                "email" to address,
                                            )
                                            newEmail = ""
                                            message = null
                                        } catch (cancelled: CancellationException) {
                                            throw cancelled
                                        } catch (failure: Throwable) {
                                            message = failure.message
                                                ?: tr("app.account.couldnt_send_that_link_retry", "Couldn't send that link. Retry?")
                                        } finally {
                                            changingEmail = false
                                        }
                                    }
                                },
                                enabled = newEmail.isNotBlank() && !changingEmail,
                                loading = changingEmail,
                            )
                            LButton(
                                t("app.account.cancel_a_pending_change", "Cancel a pending change"),
                                {
                                    scope.launch {
                                        changingEmail = true
                                        sent = null
                                        try {
                                            ApiClient.cancelEmailChange()
                                            sent = tr("app.account.the_pending_address_change_is_cancelled", "The pending address change is cancelled.")
                                            message = null
                                        } catch (cancelled: CancellationException) {
                                            throw cancelled
                                        } catch (failure: Throwable) {
                                            message = failure.message
                                                ?: tr("app.account.couldnt_cancel_that_change_retry", "Couldn't cancel that change. Retry?")
                                        } finally {
                                            changingEmail = false
                                        }
                                    }
                                },
                                variant = LButtonVariant.GHOST,
                                enabled = !changingEmail,
                            )
                        }
                    }
                }

                // The three trailing links used to live here as a bare row, duplicating — and
                // disagreeing with — the settings pill row the shell now draws above every
                // `/app/account/*` page from `AppNav.settingsHubGroupsFor`. Two lists of the same
                // destinations is one list that goes stale; the pills are the one the command
                // palette and the rail already read.
            }
        }
    }
}
